<template>
    <wrapper-features-templates-operations
    :footerButtonsParams="footerButtonsParams"
    :showFooter="!showSpinner && activeStep !== 2"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :feedbackOperationData="feedbackOperationData"
    :activeStep="activeStep"
    :scrollValue="scrollValue"
    :tokenTitleAppend="t('transfers.beneficiaries.create.tokenTitleAppend')"
    :customBreadcrumbs="customBreadcrumbs"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, createBeneficiaryRequest)"
    @clearScrollToValue="scrollValue = null"
    @parentBreadcrumbClicked="navigateByName(routeList.TRANSFERS_BENEFICIARIES_NAME)"
    @backOrCloseClicked="navigateBack()">
        <component
        :is="getActiveStepComponent"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :clearFormErrors="clearFormErrors"
        @scrollTo="scrollValue = $event"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @updateData="updateDataHandler($event)"
        @newBeneficiary="clearFields()"
        @formErrorsCleared="clearFormErrors = false"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { emailValidator, phoneNumberValidator, onlyNumbersChars } from '@/assets/scripts/validators'
import { navigateByName, navigateBack } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { createBeneficiary, clearFeatureStates } = mapActions(moduleMapper.TRANSFERS_BENEFICIARIES)

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)
const feedbackOperationData = ref(null)

const customBreadcrumbs = {
    currentArea: t('transfers.beneficiaries.create.breadcrumbs.currentArea'),
    parentMenus: [{
        id: 1,
        text: t('transfers.beneficiaries.create.breadcrumbs.firstParent')
    }, {
        id: 2,
        text: t('transfers.beneficiaries.create.breadcrumbs.secondParent'),
        internalClick: true
    }]
}

/*************************************************
*               Steps/Footer Data                *
*************************************************/
const activeStep = ref(1)

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/transfers/beneficiaries/create/Step' + stepNumber))
})

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

/*************************************************
*                                                *
*                      Data                      *
*                                                *
*************************************************/
const formData = ref({
    name: '',
    genericDestination: '',
    email: '',
    phoneNumber: '',
    address: ''
})

const mandatoryList = ref([
    'name'
])

const updateDataHandler = ({ fieldName, fieldValue }) => {
    formData.value[fieldName] = fieldValue
}

/*************************************************
*                                                *
*           Create Beneficiary Request           *
*                                                *
*************************************************/
const createBeneficiaryRequest = async () => {
    showOverlayLoader.value = true

    const payload = {
        address: formData.value.address,
        email: formData.value.email,
        genericDestination: formData.value.genericDestination.length ? formData.value.genericDestination : null,
        name: formData.value.name,
        phoneNumber: formData.value.phoneNumber.length ? formData.value.phoneNumber : null
    }

    try {
        await createBeneficiary(payload)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, createBeneficiaryRequest)
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const challengeSuccessHandler = (res) => {
    feedbackOperationData.value = {
        status: res.errorLevel,
        code: res.code,
        data: res.data,
        title: res.message
    }

    activeStep.value++
    scrollValue.value = 0
}

const errorHandler = (response, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(response, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const footerButtonsParams = computed(() => {
    return [{
        label: t('transfers.beneficiaries.create.buttonsFooterData.clear'),
        callbackFunction: clearFields,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('transfers.beneficiaries.create.buttonsFooterData.validate'),
        callbackFunction: validateInputs,
        icon: 'check-line',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 1
    }]
})

const validateInputs = () => {
    mandatoryList.value = ['name']

    if (!formData.value.genericDestination.length && !formData.value.phoneNumber.length) {
        mandatoryList.value.push('genericDestination')
        mandatoryList.value.push('phoneNumber')
    }

    atLeastOneIsEmpty(formData.value, mandatoryList.value) ||
    (!!formData.value.email.length && !emailValidator(formData.value.email)) ||
    (!!formData.value.phoneNumber.length && !phoneNumberValidator(formData.value.phoneNumber)) ||
    (!!formData.value.genericDestination.length && formData.value.genericDestination.length < 14 && onlyNumbersChars(formData.value.genericDestination)) ||
    (formData.value.genericDestination.length < 25 && !onlyNumbersChars(formData.value.genericDestination)) ? showMandatoryErrors.value = true : createBeneficiaryRequest()
}

const clearFields = () => {
    clearObjectStrings(formData.value)

    mandatoryList.value = ['name']

    clearFormErrors.value = true
    feedbackOperationData.value = null
    activeStep.value = 1
}

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
